/// <reference path="./native-syntax.d.ts" />
import { Cause, Effect, Layer, Queue, Result, Schema, Scope } from "effect";
import { EditorJsonRequest, EditorJsonDiagnostics, jsonDiagnosticMarker, makeJsonConfiguration } from "./json-model.ts";
import { EditorJsonIo, type NativeJsonWorkerAccess } from "./json-service.ts";
import { EditorLanguageInputFailure, editorLanguageDependencyFailure } from "./language-model.ts";
import { EditorPlatform } from "./platform-service.ts";
import { EditorPlatformLive } from "./platform-live.ts";
type Disposable = import("monaco-editor").IDisposable;

export const makeEditorJsonIo = Effect.fn("Frontend.EditorJsonIo.make")(function* () {
  const load = yield* Effect.cached(Effect.gen(function* () {
    const monaco = yield* Effect.tryPromise({ try: () => import("monaco-editor"), catch: editorLanguageDependencyFailure });
    const errors = yield* Effect.tryPromise({ try: () => import("monaco-editor/esm/vs/base/common/errors.js"), catch: editorLanguageDependencyFailure });
    const registry = yield* Effect.tryPromise({ try: () => import("monaco-editor/esm/vs/editor/common/languages/modesRegistry.js"), catch: editorLanguageDependencyFailure });
    const tokens = yield* Effect.tryPromise({ try: () => import("monaco-editor/esm/vs/language/json/tokenization.js"), catch: editorLanguageDependencyFailure });
    const managers = yield* Effect.tryPromise({ try: () => import("monaco-editor/esm/vs/language/json/workerManager.js"), catch: editorLanguageDependencyFailure });
    const features = yield* Effect.tryPromise({ try: () => import("monaco-editor/esm/vs/language/common/lspLanguageFeatures.js"), catch: editorLanguageDependencyFailure });
    return { monaco, errors, registry, tokens, managers, features };
  }));
  return EditorJsonIo.of({ acquire: Effect.fn("Frontend.EditorJsonIo.acquire")(function* (input) {
    const request = yield* Schema.decodeUnknownEffect(EditorJsonRequest)(input).pipe(Effect.mapError(() => new EditorLanguageInputFailure()));
    const owner = yield* Scope.Scope;
    if (owner.state._tag === "Closed") return yield* new EditorLanguageInputFailure();
    const platform = yield* load;
    const failures = yield* Queue.unbounded<import("./language-model.ts").EditorLanguageDependencyFailure>();
    yield* Effect.addFinalizer(() => Queue.clear(failures).pipe(Effect.flatMap(values => values.length
      ? Effect.failCause(Cause.fromReasons(values.flatMap(value => Cause.die(value).reasons))) : Effect.void)));
    const report = (cause: unknown) => { Queue.offerUnsafe(failures, editorLanguageDependencyFailure(cause)); };
    const capture = <A>(body: () => A) => Effect.sync(() => {
      const unexpected: unknown[] = [];
      const result = Result.try(() => platform.errors.withUnexpectedErrorHandler(cause => { unexpected.push(cause); }, body));
      return { result, unexpected };
    });
    const complete = <A>(value: Effect.Success<ReturnType<typeof capture<A>>>) => {
      const causes = [...value.unexpected, ...(Result.isFailure(value.result) ? [value.result.failure] : [])];
      return causes.length ? Effect.failCause(Cause.fromReasons(causes.flatMap(cause => Cause.fail(editorLanguageDependencyFailure(cause)).reasons)))
        : Result.isSuccess(value.result) ? Effect.succeed(value.result.success) : Effect.fail(editorLanguageDependencyFailure(value.result.failure));
    };
    const native = <A>(body: () => A) => capture(body).pipe(Effect.flatMap(complete));
    const own = <A extends Disposable>(body: () => A) => Effect.gen(function* () {
      const enrolled = yield* Effect.acquireRelease(capture(body).pipe(Effect.flatMap(value => Result.isFailure(value.result)
        ? Effect.fail(editorLanguageDependencyFailure(value.result.failure)) : Effect.succeed({ resource: value.result.success, unexpected: value.unexpected }))), value => native(() => value.resource.dispose()).pipe(Effect.orDie));
      return yield* complete({ result: Result.succeed(enrolled.resource), unexpected: enrolled.unexpected });
    });
    const reservation = yield* own(() => {
      const identifiers = new Set(platform.monaco.languages.getLanguages().map(value => value.id));
      let slot = 0; while (identifiers.has("workerstation-owned-json-" + slot)) slot++;
      const languageId = "workerstation-owned-json-" + slot;
      const registered = platform.registry.ModesRegistry.registerLanguage({ id: languageId, aliases: ["JSON"] });
      return { languageId, dispose: () => registered.dispose() };
    });
    const languageId = reservation.languageId;
    const extensions = yield* native(() => [...(platform.monaco.languages.getLanguages().find(value => value.id === "json")?.extensions ?? [])]);
    const languageFor = (filename: string) => extensions.some(extension => filename.toLowerCase().endsWith(extension.toLowerCase())) ? languageId : undefined;
    yield* own(() => platform.monaco.languages.setLanguageConfiguration(languageId, makeJsonConfiguration()));
    const defaults = platform.monaco.json.jsonDefaults;
    const manager = request._tag === "Project" ? yield* own(() => new platform.managers.WorkerManager({
      languageId, get diagnosticsOptions() { return defaults.diagnosticsOptions; }, onDidChange: defaults.onDidChange
    })) : undefined;
    const authority = request._tag === "Project" ? request.authority : undefined;
    const access: NativeJsonWorkerAccess = (...resources) => {
      if (owner.state._tag === "Closed" || !manager || authority === undefined || resources.some(resource => resource.scheme !== "file" || resource.authority !== authority))
        return Promise.reject(new EditorLanguageInputFailure());
      const result = Result.try(() => platform.errors.withUnexpectedErrorHandler(report, () => manager.getLanguageServiceWorker(...resources)));
      return Result.isFailure(result) ? Promise.reject(result.failure) : result.success.then(worker =>
        owner.state._tag === "Closed" ? Promise.reject(new EditorLanguageInputFailure()) : worker);
    };
    const callback = <A>(invoke: () => import("monaco-editor").languages.ProviderResult<A>): import("monaco-editor").languages.ProviderResult<A> => {
      if (owner.state._tag === "Closed") return undefined;
      const unexpected: unknown[] = [];
      const result = Result.try(() => platform.errors.withUnexpectedErrorHandler(cause => { unexpected.push(cause); }, invoke));
      unexpected.forEach(report);
      if (Result.isFailure(result)) { report(result.failure); return undefined; }
      // These promises come from native SDK callbacks, with no Effect runner.
      return Promise.resolve(result.success).then(value => owner.state._tag === "Closed" ? undefined : value, cause => {
        if (owner.state._tag !== "Closed") report(cause); return undefined;
      });
    };
    // This carrier contains only native SDK registration handles. Enroll it
    // before registering providers, including partially refused registrations.
    const registrations: Disposable[] = [];
    const disposeRegistrations = () => {
      while (registrations.length) {
        const resource = registrations.pop(); if (!resource) continue;
        const result = Result.try(() => resource.dispose()); if (Result.isFailure(result)) report(result.failure);
      }
    };
    yield* own(() => ({ dispose: disposeRegistrations }));
    const add = <A extends Disposable>(value: A) => { registrations.push(value); return value; };
    const rebuild = () => Result.try(() => {
      disposeRegistrations();
      const mode = defaults.modeConfiguration;
      if (mode.tokens) add(platform.monaco.languages.setTokensProvider(languageId, platform.tokens.createTokenizationSupport(true)));
      if (request._tag !== "Project") return;
      if (mode.documentFormattingEdits) {
        const provider = new platform.features.DocumentFormattingEditProvider(access);
        add(platform.monaco.languages.registerDocumentFormattingEditProvider(languageId, { provideDocumentFormattingEdits: (...args) => callback(() => provider.provideDocumentFormattingEdits(...args)) }));
      }
      if (mode.documentRangeFormattingEdits) {
        const provider = new platform.features.DocumentRangeFormattingEditProvider(access);
        add(platform.monaco.languages.registerDocumentRangeFormattingEditProvider(languageId, { provideDocumentRangeFormattingEdits: (...args) => callback(() => provider.provideDocumentRangeFormattingEdits(...args)) }));
      }
      if (mode.completionItems) {
        const provider = new platform.features.CompletionAdapter(access, [" ", ":", '"']);
        add(platform.monaco.languages.registerCompletionItemProvider(languageId, { triggerCharacters: provider.triggerCharacters, provideCompletionItems: (...args) => callback(() => provider.provideCompletionItems(...args)) }));
      }
      if (mode.hovers) {
        const provider = new platform.features.HoverAdapter(access);
        add(platform.monaco.languages.registerHoverProvider(languageId, { provideHover: (...args) => callback(() => provider.provideHover(...args)) }));
      }
      if (mode.documentSymbols) {
        const provider = new platform.features.DocumentSymbolAdapter(access);
        add(platform.monaco.languages.registerDocumentSymbolProvider(languageId, { provideDocumentSymbols: (...args) => callback(() => provider.provideDocumentSymbols(...args)) }));
      }
      if (mode.colors) {
        const provider = new platform.features.DocumentColorAdapter(access);
        add(platform.monaco.languages.registerColorProvider(languageId, { provideDocumentColors: (...args) => callback(() => provider.provideDocumentColors(...args)), provideColorPresentations: (...args) => callback(() => provider.provideColorPresentations(...args)) }));
      }
      if (mode.foldingRanges) {
        const provider = new platform.features.FoldingRangeAdapter(access);
        add(platform.monaco.languages.registerFoldingRangeProvider(languageId, { provideFoldingRanges: (...args) => callback(() => provider.provideFoldingRanges(...args)) }));
      }
      if (mode.selectionRanges) {
        const provider = new platform.features.SelectionRangeAdapter(access);
        add(platform.monaco.languages.registerSelectionRangeProvider(languageId, { provideSelectionRanges: (...args) => callback(() => provider.provideSelectionRanges(...args)) }));
      }
      if (mode.diagnostics) {
        let active = true;
        class OwnedDiagnostics extends platform.features.DiagnosticsAdapter {
          override _doValidate(resource: import("monaco-editor").Uri, selector: string): void {
            if (!active || owner.state._tag === "Closed") return;
            const observed = Result.try(() => platform.errors.withUnexpectedErrorHandler(report, () => {
              const model = platform.monaco.editor.getModel(resource);
              return model && model.getLanguageId() === languageId && resource.authority === authority ? { model, version: model.getVersionId() } : undefined;
            }));
            if (Result.isFailure(observed)) { report(observed.failure); return; }
            if (!observed.success) return;
            const { model, version } = observed.success;
            // Avoid the SDK's console.error path for an unknown validation cause.
            access(resource).then(worker => worker.doValidation(resource.toString())).then(raw => {
              callback(() => {
                if (!active || platform.monaco.editor.getModel(resource) !== model || model.getVersionId() !== version) return;
                const decoded = Schema.decodeUnknownResult(EditorJsonDiagnostics)(raw);
                if (Result.isFailure(decoded)) { report(decoded.failure); return; }
                platform.monaco.editor.setModelMarkers(model, selector, decoded.success.map(jsonDiagnosticMarker));
              });
            }, cause => { if (active && owner.state._tag !== "Closed") report(cause); });
          }
        }
        const diagnostics = new OwnedDiagnostics(languageId, access, defaults.onDidChange);
        add({ dispose: () => { active = false; diagnostics.dispose(); } });
        const reset = (model: import("monaco-editor").editor.ITextModel) => callback(() => {
          if (model.getLanguageId() !== languageId || model.uri.authority !== authority) return;
          return access(model.uri).then(worker => worker.resetSchema(model.uri.toString()));
        });
        add(platform.monaco.editor.onWillDisposeModel(reset));
        add(platform.monaco.editor.onDidChangeModelLanguage(event => { callback(() => {
          if (event.model.uri.authority !== authority || event.oldLanguage !== languageId) return;
          return access(event.model.uri).then(worker => worker.resetSchema(event.model.uri.toString()));
        }); }));
      }
    });
    const initial = yield* native(rebuild);
    if (Result.isFailure(initial)) return yield* Effect.fail(editorLanguageDependencyFailure(initial.failure));
    let previousMode = yield* native(() => defaults.modeConfiguration);
    yield* own(() => defaults.onDidChange(() => { callback(() => {
      if (defaults.modeConfiguration === previousMode) return;
      previousMode = defaults.modeConfiguration;
      const result = rebuild(); if (Result.isFailure(result)) report(result.failure);
    }); }));
    return { languageId, languageFor };
  }) });
});

export const EditorJsonIoLive = Layer.effect(EditorJsonIo, Effect.gen(function* () {
  const platform = yield* EditorPlatform, io = yield* makeEditorJsonIo();
  return EditorJsonIo.of({ acquire: Effect.fn("Frontend.EditorJsonIo.liveAcquire")(function* (request) {
    yield* platform.ready.pipe(Effect.mapError(editorLanguageDependencyFailure));
    return yield* io.acquire(request);
  }) });
})).pipe(Layer.provide(EditorPlatformLive));
